import { Badge } from "@/components/ui/badge";
import { STATUS_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, hint, tone = "primary", testId }) {
  const tones = {
    primary: "text-primary bg-primary/10",
    success: "text-emerald-500 bg-emerald-500/10",
    warning: "text-amber-500 bg-amber-500/10",
    teal: "text-teal-500 bg-teal-500/10",
    danger: "text-red-500 bg-red-500/10",
  };
  return (
    <div className="card-surface card-hover p-5 fade-up" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <p className="eyebrow">{label}</p>
        {Icon && (
          <span className={cn("grid h-9 w-9 place-items-center rounded-lg", tones[tone])}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <p className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="card-surface grain flex flex-col items-center justify-center gap-3 px-6 py-14 text-center" data-testid="empty-state">
      {Icon && <Icon className="h-10 w-10 text-muted-foreground/60" />}
      <p className="font-display text-lg font-semibold">{title}</p>
      {text && <p className="max-w-sm text-sm text-muted-foreground">{text}</p>}
      {action}
    </div>
  );
}

const STATUS_TONE = {
  trial: "bg-primary/15 text-primary ring-primary/30",
  active: "bg-emerald-500/15 text-emerald-500 ring-emerald-500/30",
  expired: "bg-amber-500/15 text-amber-500 ring-amber-500/30",
  suspended: "bg-red-500/15 text-red-500 ring-red-500/30",
  cancelled: "bg-red-500/15 text-red-500 ring-red-500/30",
};

export function PlanStatusBadge({ status, testId = "plan-status-badge" }) {
  return (
    <Badge variant="outline" className={cn("font-mono text-[10px] uppercase tracking-wider ring-1 border-0", STATUS_TONE[status])} data-testid={testId}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export function Field({ label, children, hint, className }) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}
